import { type FC, useCallback, useMemo, useState } from "react";
import { Platform, View } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import type { CountryCode } from "libphonenumber-js";
import "../../theme";
import { useCombobox } from "../../hooks/use-combobox";
import { Input } from "../Input";
import type { InputSize } from "../Input/types";
import { CountryPickerDropdown } from "./parts/CountryPickerDropdown";
import { CountryPickerSheet } from "./parts/CountryPickerSheet";
import { CountryTrigger } from "./parts/CountryTrigger";
import {
  type CountryOption,
  type PhoneInputValue,
  filterCountryOptions,
  getCountryOptions,
  withTypedText,
} from "./phone-number";

export interface PhoneInputProps {
  /** Selected country and the number as typed (controlled). */
  readonly value: PhoneInputValue;
  /** Called when the country or the typed number changes. */
  readonly onChange: (value: PhoneInputValue) => void;
  /** Countries listed first in the picker, in this order (e.g. the regions the product serves). */
  readonly pinnedCountries?: readonly CountryCode[] | undefined;
  /** Floating label rendered in the border notch. */
  readonly label?: string | undefined;
  /** Placeholder for the number part, shown while it is empty. */
  readonly placeholder?: string | undefined;
  /** Error message shown below the field. */
  readonly error?: string | undefined;
  /** Neutral helper text shown below the field (ignored when `error` is set). */
  readonly helperText?: string | undefined;
  /** Disables the number and the country picker. */
  readonly disabled?: boolean | undefined;
  /** Size variant. Defaults to `medium`. */
  readonly size?: InputSize | undefined;
  /** Shows a clear button when a number has been typed. */
  readonly clearable?: boolean | undefined;
  /** Called when the clear button is pressed. Defaults to clearing the number via `onChange`. */
  readonly onClear?: (() => void) | undefined;
  /** Called when the number field gains focus. */
  readonly onFocus?: (() => void) | undefined;
  /** Called when the number field loses focus. */
  readonly onBlur?: (() => void) | undefined;
  /** Heading of the country picker on native. Defaults to "Country code". */
  readonly countryPickerTitle?: string | undefined;
  /** Accessibility label for the number field. */
  readonly accessibilityLabel?: string | undefined;
}

// Matches Input's SIZE_HEIGHT plus the gap Autocomplete leaves under its field.
const DROPDOWN_TOP: Record<InputSize, number> = { small: 40, medium: 48 };

// Keyed by the pinned list's contents rather than its identity, so a consumer
// passing an inline array literal doesn't re-sort ~245 countries every render.
const optionsCache = new Map<string, readonly CountryOption[]>();
function countryOptionsFor(pinned: readonly CountryCode[]): readonly CountryOption[] {
  const key = pinned.join(",");
  const cached = optionsCache.get(key);
  if (cached) return cached;
  const options = getCountryOptions(pinned);
  optionsCache.set(key, options);
  return options;
}

/**
 * Phone number field with a country dial-code selector inside it, on the
 * left. The user types the number in local form (`0412 345 678`); pasting a
 * full international number moves the selector to match. Convert the value
 * with `phoneInputValueToE164` to validate and store it.
 *
 * The country picker is an anchored, keyboard-navigable dropdown on web and a
 * full-height searchable sheet on native.
 */
const PhoneInput: FC<PhoneInputProps> = ({
  value,
  onChange,
  pinnedCountries = [],
  label,
  placeholder,
  error,
  helperText,
  disabled = false,
  size = "medium",
  clearable = false,
  onClear,
  onFocus,
  onBlur,
  countryPickerTitle = "Country code",
  accessibilityLabel,
}) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [query, setQuery] = useState("");

  const allOptions = countryOptionsFor(pinnedCountries);
  const filteredOptions = useMemo(() => filterCountryOptions(allOptions, query), [allOptions, query]);
  const selectedOption = useMemo(
    () => allOptions.find((o) => o.country === value.country) ?? allOptions[0],
    [allOptions, value.country],
  );

  const handleOpenChange = useCallback((open: boolean) => {
    setIsPickerOpen(open);
    if (!open) setQuery("");
  }, []);

  const handleSelectCountry = useCallback(
    (option: CountryOption) => {
      onChange({ country: option.country, nationalNumber: value.nationalNumber });
      handleOpenChange(false);
    },
    [onChange, value.nationalNumber, handleOpenChange],
  );

  const { containerRef, highlightedIndex, setHighlightedIndex, handleKeyPress } = useCombobox<CountryOption>({
    options: filteredOptions,
    getOptionId: (o) => o.country,
    onSelect: handleSelectCountry,
    isOpen: isPickerOpen,
    onOpenChange: handleOpenChange,
  });

  const handleChangeText = useCallback((text: string) => onChange(withTypedText(value, text)), [onChange, value]);

  if (selectedOption === undefined) return null;

  const isWeb = Platform.OS === "web";

  return (
    <View ref={containerRef} style={[styles.container, isPickerOpen && isWeb && styles.containerOpen]}>
      <Input
        value={value.nationalNumber}
        onChangeText={handleChangeText}
        label={label}
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        disabled={disabled}
        size={size}
        active={isPickerOpen}
        clearable={clearable}
        onClear={onClear}
        onFocus={onFocus}
        onBlur={onBlur}
        leftContent={
          <CountryTrigger
            option={selectedOption}
            isOpen={isPickerOpen}
            disabled={disabled}
            onPress={() => handleOpenChange(!isPickerOpen)}
          />
        }
        // Input applies its own `accessibilityLabel` only in trigger mode, so name the text input directly.
        textInputProps={{
          accessibilityLabel: accessibilityLabel ?? label,
          keyboardType: "phone-pad",
          textContentType: "telephoneNumber",
          autoComplete: "tel",
        }}
      />

      {isWeb ?
        isPickerOpen && (
          <CountryPickerDropdown
            top={DROPDOWN_TOP[size]}
            query={query}
            onQueryChange={setQuery}
            onKeyPress={handleKeyPress}
            options={filteredOptions}
            selected={value.country}
            highlightedIndex={highlightedIndex}
            onSelect={handleSelectCountry}
            onHighlight={setHighlightedIndex}
          />
        )
      : <CountryPickerSheet
          isOpen={isPickerOpen}
          onClose={() => handleOpenChange(false)}
          title={countryPickerTitle}
          query={query}
          onQueryChange={setQuery}
          options={filteredOptions}
          selected={value.country}
          onSelect={handleSelectCountry}
        />
      }
    </View>
  );
};

PhoneInput.displayName = "PhoneInput";

export { PhoneInput };

const styles = StyleSheet.create((theme) => ({
  container: {
    position: "relative",
    overflow: "visible",
  },
  containerOpen: {
    zIndex: theme.zIndex.inputOpen,
  },
}));
